import {
  DashboardData,
  Percent,
  PERCENT_ZERO,
  SKELETON_DASHBOARD_MARKET,
  USD_ZERO,
} from "@silo/types";
import { cn } from "../../utils/class-names";
import { ReactNode } from "react";
import { LendingPosition } from "../lending-position";
import { Totals } from "./totals";
import { SkeletonWrapper } from "../skeleton";
import { Array as EffectArray, Option, pipe } from "effect";
import { AT_RISK_OF_LIQUIDATION_ID } from "./constants";
import { NoPositions } from "./no-positions";
import { Wallet } from "../wallet";
import { getIsRevenueMarket } from "@silo/utils";

export interface DashboardPositionsProps {
  className?: string;
  dashboardData: DashboardData;
  tokenLogo: ReactNode;
  depositMoreLink: ReactNode;
  withdrawLink: ReactNode;
  borrowMoreLink: ReactNode;
  repayLink: ReactNode;
  borrowLink: ReactNode;
  revenueApr: Percent;
}

export function DashboardPositions({
  className,
  dashboardData,
  tokenLogo,
  depositMoreLink,
  withdrawLink,
  borrowLink,
  borrowMoreLink,
  repayLink,
  revenueApr,
}: DashboardPositionsProps): JSX.Element {
  const {
    data: { depositBalanceUsd, debtBalanceUsd, dashboardMarkets, protocolKey },
  } = dashboardData;

  const firstMarketAtRiskOfLiquidation = pipe(
    dashboardMarkets,
    EffectArray.findFirst((market) => market.isAtRiskOfLiquidation),
    Option.map((market) => market.marketAddress),
    Option.getOrUndefined
  );

  const hasPositions = dashboardMarkets.length > 0;

  return (
    <Root className={className}>
      <SectionContainer>
        <Header />

        <Totals
          depositBalanceUsd={depositBalanceUsd}
          debtBalanceUsd={debtBalanceUsd}
        />
      </SectionContainer>

      <SectionContainer>
        {hasPositions ? (
          dashboardMarkets.map((dashboardMarket) => {
            const { marketAddress } = dashboardMarket;

            const id =
              marketAddress === firstMarketAtRiskOfLiquidation
                ? AT_RISK_OF_LIQUIDATION_ID
                : undefined;

            const effectiveRevenueApr = getIsRevenueMarket(
              protocolKey,
              marketAddress
            )
              ? revenueApr
              : PERCENT_ZERO;

            return (
              <LendingPosition
                dashboardMarket={dashboardMarket}
                tokenLogo={tokenLogo}
                includeTitle
                depositMoreLink={depositMoreLink}
                withdrawLink={withdrawLink}
                borrowMoreLink={borrowMoreLink}
                repayLink={repayLink}
                borrowLink={borrowLink}
                id={id}
                revenueApr={effectiveRevenueApr}
              />
            );
          })
        ) : (
          <NoPositions.Root>
            <NoPositions.Heading>
              You don't have any positions yet.
            </NoPositions.Heading>

            <NoPositions.Description>
              Deposit to one of the silos to see your position here.
            </NoPositions.Description>
          </NoPositions.Root>
        )}
      </SectionContainer>
    </Root>
  );
}

const {
  lendingLink,
  tokenLogo,
  useLendingLinkContext,
  DepositMoreLinkContent,
  WithdrawLinkContent,
  BorrowMoreLinkContent,
  RepayLinkContent,
  BorrowLinkContent,
} = LendingPosition;

DashboardPositions.lendingLink = lendingLink;
DashboardPositions.tokenLogo = tokenLogo;
DashboardPositions.useLendingLinkContext = useLendingLinkContext;
DashboardPositions.DepositMoreLinkContent = DepositMoreLinkContent;
DashboardPositions.WithdrawLinkContent = WithdrawLinkContent;
DashboardPositions.BorrowMoreLinkContent = BorrowMoreLinkContent;
DashboardPositions.RepayLinkContent = RepayLinkContent;
DashboardPositions.BorrowLinkContent = BorrowLinkContent;
DashboardPositions.Pulse = Pulse;
DashboardPositions.Disconnected = Disconnected;
DashboardPositions.AT_RISK_OF_LIQUIDATION_ID = AT_RISK_OF_LIQUIDATION_ID;

interface RootProps {
  className?: string;
  children?: ReactNode;
}

function Root({ className, children }: RootProps): JSX.Element {
  return (
    <div className={cn("flex flex-col gap-16", className)}>{children}</div>
  );
}

interface SectionContainerProps {
  className?: string;
  children?: ReactNode;
}

function SectionContainer({
  className,
  children,
}: SectionContainerProps): JSX.Element {
  return (
    <div className={cn("flex flex-col gap-10", className)}>{children}</div>
  );
}

function Header(): JSX.Element {
  return (
    <h1 className="text-lg font-medium text-white sm:text-2xl">
      My open positions
    </h1>
  );
}

interface PulseProps {
  className?: string;
}

function Pulse({ className }: PulseProps): JSX.Element {
  return (
    <Root className={cn("animate-pulse", className)}>
      <SectionContainer className="items-start">
        <Header />

        <SkeletonWrapper>
          <Totals depositBalanceUsd={USD_ZERO} debtBalanceUsd={USD_ZERO} />
        </SkeletonWrapper>
      </SectionContainer>

      <SectionContainer>
        {[...Array(5)].map(() => (
          <SkeletonWrapper>
            <LendingPosition
              dashboardMarket={SKELETON_DASHBOARD_MARKET}
              tokenLogo={<div className={tokenLogo()}></div>}
              depositMoreLink={<DepositMoreLinkContent />}
              withdrawLink={<WithdrawLinkContent />}
              borrowMoreLink={<BorrowMoreLinkContent />}
              repayLink={<RepayLinkContent />}
              borrowLink={<BorrowLinkContent />}
              revenueApr={PERCENT_ZERO}
            />
          </SkeletonWrapper>
        ))}
      </SectionContainer>
    </Root>
  );
}

interface DisconnectedProps {
  className?: string;
}

function Disconnected({ className }: DisconnectedProps): JSX.Element {
  return (
    <Root className={className}>
      <SectionContainer>
        <Header />

        <Totals depositBalanceUsd={null} debtBalanceUsd={null} />
      </SectionContainer>

      <SectionContainer>
        <NoPositions.Root>
          <NoPositions.Heading>Connect your wallet</NoPositions.Heading>

          <NoPositions.Description>
            Please connect your wallet to see your open positions.
          </NoPositions.Description>

          <Wallet.ConnectWallet className="mt-8" />
        </NoPositions.Root>
      </SectionContainer>
    </Root>
  );
}
